import { IS_SINGLE_HOST } from '@/config/env';
import { buildWorkspaceUrl, getWorkspaceSlug } from './getWorkspaceSlug';

/**
 * Where a mailed link with `?ws=<slug>` should really go.
 *
 * The backend names the workspace in every emailed app link. On a subdomain
 * deployment it does so in the host (`acme.clarix…/projects/my-timesheet`), and
 * nothing here runs. On an APEX deployment the link is `clarix…/projects/my-timesheet?ws=acme`:
 * the apex has no workspace, so the shell would show the workspace picker and,
 * after sign-in, the launcher — never the page the mail named.
 *
 * This hops that link to `acme.<root>` with the path, remaining query and
 * fragment intact, so the normal sign-in `next` flow lands on the exact page.
 *
 * Returns null when no hop is needed: single-host deploys (the session holds the
 * workspace), a host that already names one, or no/invalid `ws`.
 */
export function workspaceLinkRedirect(
  loc: { protocol: string; hostname: string; port: string; pathname: string; search: string; hash: string } = window.location,
): string | null {
  if (IS_SINGLE_HOST) return null;
  if (getWorkspaceSlug(loc.hostname)) return null;

  const params = new URLSearchParams(loc.search);
  const raw = params.get('ws');
  if (!raw) return null;
  // Same shape the picker normalises to — a hand-edited link cannot inject a host.
  const slug = raw.trim().toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 63);
  if (!slug) return null;

  params.delete('ws');
  const rest = params.toString();
  return buildWorkspaceUrl(slug, `${loc.pathname}${rest ? `?${rest}` : ''}${loc.hash}`, loc);
}
